import Image from 'components/Common/Image/Image'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import ModalContext from 'contexts/ModalContext'
import TextButton from 'LuxKit/Button/TextButton'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import LineImagesIcon from 'LuxKit/Icons/line/LineImagesIcon'
import type { MouseEventHandler, ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import {
  DRAWER_DETAIL_MOBILE_FULL_WIDTH_CLASS,
  GalleryButtons,
  GridContainer,
  GridImageClickable,
  PRIMARY_GALLERY_IMAGE_CLASS,
  SECONDARY_GALLERY_IMAGE_CLASS,
} from '../components/TripPlanDetailDrawer.styles'
import { DrawerDetailHeroLoading } from '../components/drawerDetailHeroLayout'
import { getDrawerGalleryImageKey, useDrawerGalleryImages } from './useDrawerGalleryImages'

const mainImageProps: ImageParams = { tabletWidth: '800px', quality: 'best' }
const defaultImageProps: ImageParams = { tabletWidth: '400px', quality: 'good' }

interface Args {
  images: Array<App.Image>
  resetKey: string
  isLoading: boolean
  mobileFullWidth: boolean
  previewImageCount?: number
  viewGalleryLabel: string
}

/**
 * Builds the hero gallery shared by the trip planner detail drawers so callers
 * can place it above their own content (title, tabs) instead of inside a tab.
 */
export function useDrawerDetailHeroGallery({
  images,
  resetKey,
  isLoading,
  mobileFullWidth,
  previewImageCount,
  viewGalleryLabel,
}: Args): ReactNode {
  const showModal = useContext(ModalContext)

  const {
    availableImages,
    gridImages,
    imageCount,
    handleImageError,
  } = useDrawerGalleryImages({ images, resetKey, previewImageCount })

  const handleGalleryImageClick = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    const imageIndex = Number(event.currentTarget.dataset.index) || 0
    showModal(<ImageGalleryLightbox images={availableImages} defaultIndex={imageIndex} />)
  }, [availableImages, showModal])

  const openGalleryLightbox = useCallback(() => {
    showModal(<ImageGalleryLightbox images={availableImages} defaultIndex={0} />)
  }, [availableImages, showModal])

  return useMemo(() => {
    if (isLoading) {
      return <DrawerDetailHeroLoading mobileFullWidth={mobileFullWidth} />
    }

    if (gridImages.length === 0) {
      return null
    }

    return (
      <GridContainer
        $imageCount={imageCount}
        $matchOfferPageAspect
        className={mobileFullWidth ? DRAWER_DETAIL_MOBILE_FULL_WIDTH_CLASS : undefined}
      >
        {gridImages.map(({ image, lightboxIndex }, gridImageIndex) => (
          <GridImageClickable
            className={gridImageIndex === 0 ? PRIMARY_GALLERY_IMAGE_CLASS : SECONDARY_GALLERY_IMAGE_CLASS}
            key={image.id ?? image.url}
            onClick={handleGalleryImageClick}
            data-index={lightboxIndex}
          >
            {image.id && <ResponsiveImage
              id={image.id}
              fit="center"
              data-image-key={getDrawerGalleryImageKey(image)}
              onError={handleImageError}
              {...(gridImageIndex === 0 ? mainImageProps : defaultImageProps)}
            />}
            {image.url && <Image
              image={image}
              fit="center"
              data-image-key={getDrawerGalleryImageKey(image)}
              onError={handleImageError}
            />}
          </GridImageClickable>
        ))}
        {availableImages.length > 1 && (
          <GalleryButtons direction="horizontal" gap={8} horizontalAlign="end">
            <TextButton
              kind="secondary"
              variant="dark"
              startIcon={<LineImagesIcon />}
              onClick={openGalleryLightbox}
            >
              {viewGalleryLabel}
            </TextButton>
          </GalleryButtons>
        )}
      </GridContainer>
    )
  }, [
    availableImages.length,
    gridImages,
    handleGalleryImageClick,
    handleImageError,
    imageCount,
    isLoading,
    mobileFullWidth,
    openGalleryLightbox,
    viewGalleryLabel,
  ])
}
